import React from 'react';
import PropTypes from 'prop-types';
import Dialogs from "./Dialogs.js";
import Pane from "./Pane.js";
import Flex from "./Flex.js";
import Button from "./Button.js";
import Text from "./Text.js";
import Content from "./Content.js";
import Theme from "./Theme.js";
import PortalOverlay from "./PortalOverlay.js";
import S from './Dialog.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = Theme.I18N.ns('Dialog');

/**
 *  React class that renders a popup with a message or a question 
 * @property {string} variant - Theme variant name to be added to the component
 * @property {boolean} show - if true, the popup will be displayed on the page
 * @property {string} title - the title of the popup
 * @property {string} icon - the icon for the title, either url or font icon className
 * @property {function} onClose - function to be executed when the popup gets closed
 * @property {boolean} closeOnEscape - if true, popup will close when the esc key is hit
 * @property {boolean} closeOnDocumentClick - if true popup will close when the document is clicked
 * @property {function} onSubmit - function to be ran when the question answer is submitted 
 * @property {object} formProps - if there is an onSubmit function provided, a form will wrap the children, and this data will be passed into the form
 * @property {node} children - React children of the element
 * */
class Dialog extends React.Component {
  static contextType = Theme.Context;

  /* eslint-disable react/require-default-props */
  static propTypes = {
    variant: PropTypes.string,
    show: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    icon: PropTypes.string,
    onClose: PropTypes.func,
    closeOnEscape: PropTypes.bool,
    closeOnDocumentClick: PropTypes.bool,
    onSubmit: PropTypes.func,
    formProps: PropTypes.object,
    children: PropTypes.node
  };
  onClose = e => {
    const {
      eventData,
      onClose
    } = this.props;
    if (typeof onClose === 'function') {
      onClose({
        data: eventData,
        sourceEvent: e
      });
    } else {
      console.warn('No onClose function supplied to Dialog component');
    }
  };

  /* eslint-enable react/require-default-props */
  render() {
    const {
      variant = 'dialog',
      portalVariant = 'dialog',
      headerVariant = 'dialogHeader',
      bodyVariant = 'dialogBody',
      dialogTitleVariant = 'dialogTitle',
      footerVariant = 'dialogFooter',
      contentVariant = null,
      buttonLayoutVariant,
      show,
      title,
      icon,
      footer,
      buttons,
      className,
      closeOnEscape = true,
      closeOnDocumentClick = true,
      position,
      onSubmit,
      formProps = {},
      children,
      animation,
      headerClassName,
      bodyClassName,
      footerClassName,
      portalTargetId
    } = this.props;

    // No variant for this? set defaults
    const theme = this.context;
    const hasVariant = theme.resolveVariant('Portal', portalVariant) != null;
    const a = hasVariant ? animation : animation || 'slideFromTop';
    const p = hasVariant ? position : position || 'center';
    const custom = !onSubmit && title == null && buttons == null && footer == null;
    const bl = buttonLayoutVariant ? `${buttonLayoutVariant}|dialogButtons|rowM|row` : 'dialogButtons|rowM|row';
    const opts = {};
    if (a !== undefined) {
      opts.animation = a;
    }
    if (p !== undefined) {
      opts.position = p;
    }
    // TODO: overlayStyle into variant. can we use css?
    return /*#__PURE__*/_jsx(PortalOverlay, {
      show: show,
      ...opts,
      clickWillClose: closeOnDocumentClick,
      escapeWillClose: closeOnEscape,
      variant: portalVariant,
      onClose: this.onClose,
      targetId: portalTargetId,
      children: /*#__PURE__*/_jsxs(Pane, {
        className: className,
        variant: variant,
        children: [onSubmit &&
        /*#__PURE__*/
        /* Maybe deprecate the onSubmit Path */
        _jsx("form", {
          onSubmit: onSubmit,
          ...formProps,
          children: /*#__PURE__*/_jsxs(Flex, {
            direction: "column",
            children: [typeof title === 'string' && /*#__PURE__*/_jsx(Text, {
              variant: dialogTitleVariant,
              icon: icon,
              children: title
            }), children]
          })
        }), custom && /*#__PURE__*/_jsx(Flex, {
          direction: "column",
          children: children
        }), !onSubmit && !custom && /*#__PURE__*/_jsxs(Content, {
          relative: true,
          className: S.dialogContent,
          variant: contentVariant,
          children: [title && /*#__PURE__*/_jsxs(Content.Header, {
            className: headerClassName,
            variant: headerVariant,
            children: [typeof title === 'string' && /*#__PURE__*/_jsx(Text, {
              variant: dialogTitleVariant,
              icon: icon,
              children: title
            }), typeof title !== 'string' && title]
          }), /*#__PURE__*/_jsx(Content.Area, {
            className: bodyClassName,
            variant: bodyVariant,
            children: children
          }), (buttons || footer) && /*#__PURE__*/_jsx(Content.Footer, {
            className: footerClassName,
            variant: footerVariant,
            children: footer != null ? footer : /*#__PURE__*/_jsx(Flex, {
              variant: bl,
              children: buttons
            })
          })]
        })]
      })
    });
  }
}

/**
 * React class to render a dialog message
 * @property {string} variant - Theme variant name to be added to the component 
 * @property {string} okayVariant - Theme variant name to be applied to the dialog okay button
 * @property {string} title - the title of the popup 
 * @property {string} okay - the text that will be on the okay button 
 * @property {boolean} show - if true, the popup will be displayed on the page 
 * @property {function} onClose - function to be executed when the popup gets closed 
 * @property {string} message - text or react element to be displayed to the user 
 * @property {node} children - React children of the element 
 */
class Message extends React.Component {
  /* eslint-disable react/require-default-props */
  static propTypes = {
    variant: PropTypes.string,
    okayVariant: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    okay: PropTypes.string,
    show: PropTypes.bool,
    onClose: PropTypes.func,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node
  };
  /* eslint-enable react/require-default-props */

  static async show(props) {
    return new Promise(resolve => {
      Dialogs.show({
        component: props => {
          const {
            pOonClose
          } = props;
          const onClose = () => {
            resolve(true);
            pOonClose();
          };
          const onOkay = () => {
            resolve(true);
            pOonClose();
          };
          return /*#__PURE__*/_jsx(Message, {
            ...props,
            onClose: onClose,
            onOkay: onOkay
          });
        },
        props: {
          ...props,
          resolve
        }
      });
    });
  }
  render() {
    const {
      variant = 'dialog',
      okayVariant = 'dialog',
      buttonLayoutVariant,
      headerVariant = 'questionHeader|dialogHeader',
      bodyVariant = 'questionBody|dialogBody',
      dialogTitleVariant = 'questionTitle|dialogTitle',
      footerVariant = 'questionFooter|dialogFooter',
      title,
      icon,
      okay = t('okay', 'Okay'),
      show = false,
      eventData,
      onClose,
      message,
      children,
      ...rest
    } = this.props;
    const msg = typeof message === 'string' ? /*#__PURE__*/_jsx(Text, {
      variant: "dialogMessage",
      children: message
    }) : children;
    const buttons = /*#__PURE__*/_jsx(Button, {
      variant: okayVariant,
      eventData: eventData,
      onClick: onClose,
      children: okay
    });
    const bl = buttonLayoutVariant ? `${buttonLayoutVariant}|messageButtons` : 'messageButtons';
    return /*#__PURE__*/_jsx(Dialog, {
      variant: variant,
      show: show,
      headerVariant: headerVariant,
      bodyVariant: bodyVariant,
      footerVariant: footerVariant,
      dialogTitleVariant: dialogTitleVariant,
      buttonLayoutVariant: bl,
      eventData: eventData,
      onClose: onClose,
      title: title,
      icon: icon,
      ...rest,
      buttons: buttons,
      children: msg
    });
  }
}

/**
 * React class to render a Dialog question
 * @property {string} variant - Theme variant name to be added to the component 
 * @property {string} okayVariant - Theme variant name to be applied to the dialog okay button
 * @property {string} cancelVariant - Theme variant name to be applied to the dialog cancel button
 * @property {string} title - the title of the popup 
 * @property {string} okay - the text that will be on the okay button 
 * @property {string} cancel - the text that will be on the cancel button 
 * @property {any} eventData - will be passed through as the data key to the onClick function 
 * @property {boolean} show - if true, the popup will be displayed on the page 
 * @property {function} onOkay - function to be run on hitting okay 
 * @property {function} onCancel - function to be executed when the cancel button on the popup is clicked 
 * @property {string} message - text or react element to be displayed to the user 
 * @property {node} children - React children of the element 
 */
class Question extends React.Component {
  /* eslint-disable react/require-default-props */
  static propTypes = {
    variant: PropTypes.string,
    okayVariant: PropTypes.string,
    cancelVariant: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    icon: PropTypes.string,
    okay: PropTypes.string,
    cancel: PropTypes.string,
    eventData: PropTypes.any,
    show: PropTypes.bool,
    onOkay: PropTypes.func,
    onCancel: PropTypes.func,
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node
  };
  /* eslint-enable react/require-default-props */

  static async ask(props) {
    return new Promise(resolve => {
      Dialogs.show({
        component: props => {
          const {
            onClose
          } = props;
          const onCancel = () => {
            resolve(false);
            onClose();
          };
          const onOkay = () => {
            resolve(true);
            onClose();
          };
          return /*#__PURE__*/_jsx(Question, {
            ...props,
            onCancel: onCancel,
            onOkay: onOkay
          });
        },
        props: {
          ...props,
          resolve
        }
      });
    });
  }
  render() {
    const {
      variant = 'dialog',
      okayVariant = 'dialog',
      cancelVariant = 'dialogCancel|secondary',
      title,
      icon,
      okay = t('okay', 'Okay'),
      cancel = t('cancel', 'Cancel'),
      eventData,
      buttonLayoutVariant,
      headerVariant = 'questionHeader|dialogHeader',
      bodyVariant = 'questionBody|dialogBody',
      dialogTitleVariant = 'questionTitle|dialogTitle',
      footerVariant = 'questionFooter|dialogFooter',
      show = false,
      onOkay,
      onCancel,
      message,
      children,
      okayDisabled,
      cancelDisabled,
      ...rest
    } = this.props;
    const msg = typeof message === 'string' ? /*#__PURE__*/_jsx(Text, {
      variant: "dialogMessage",
      children: message
    }) : children;
    const bl = buttonLayoutVariant ? `${buttonLayoutVariant}|questionButtons` : 'questionButtons';
    const buttons = [/*#__PURE__*/_jsx(Button, {
      variant: cancelVariant,
      onClick: onCancel,
      eventData: eventData,
      disabled: cancelDisabled,
      children: cancel
    }, 2),
    /*#__PURE__*/
    // eslint-disable-line max-len
    _jsx(Button, {
      variant: okayVariant,
      onClick: onOkay,
      eventData: eventData,
      disabled: okayDisabled,
      children: okay
    }, 1) // eslint-disable-line max-len
    ];
    return /*#__PURE__*/_jsx(Dialog, {
      variant: variant,
      show: show,
      headerVariant: headerVariant,
      bodyVariant: bodyVariant,
      footerVariant: footerVariant,
      dialogTitleVariant: dialogTitleVariant,
      eventData: eventData,
      onClose: onCancel,
      title: title,
      icon: icon,
      buttonLayoutVariant: bl,
      ...rest,
      buttons: buttons,
      children: msg
    });
  }
}
Dialog.Message = Message;
Dialog.Question = Question;
export default Dialog;